import type { PostHogInterface } from 'posthog-js'
import type { ChatLanding, ResolvedExperience } from '../types/content'
import type { PovliEvent } from '../types/analytics'
import { getAttribution } from './attribution'
import { captureMetaEvent, initMetaPixel } from './meta-pixel'
import { getSessionId } from './session'

interface QueuedEvent {
  event: PovliEvent
  properties: Record<string, string | number | boolean | null>
}

let client: PostHogInterface | undefined
let enabled = false
const queue: QueuedEvent[] = []

export function initAnalytics(): boolean {
  const metaEnabled = initMetaPixel()
  const apiKey = import.meta.env.VITE_POSTHOG_KEY?.trim()
  if (!apiKey || apiKey === 'phc_replace_me') return metaEnabled

  enabled = true
  void import('posthog-js').then(({ default: posthog }) => {
    posthog.init(apiKey, {
      api_host: import.meta.env.VITE_POSTHOG_HOST?.trim() || 'https://us.i.posthog.com',
      ui_host: import.meta.env.VITE_POSTHOG_UI_HOST?.trim() || 'https://us.posthog.com',
      defaults: '2026-05-30',
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      disable_session_recording: true,
      person_profiles: 'identified_only',
      persistence: 'memory',
      opt_out_capturing_by_default: false,
      loaded: (loadedClient) => {
        client = loadedClient
        for (const queued of queue.splice(0)) loadedClient.capture(queued.event, queued.properties)
      },
    })
  }).catch(() => {
    enabled = false
    queue.length = 0
  })
  return true
}

export function captureStoryEvent(event: PovliEvent, experience: ResolvedExperience, extra: Record<string, string | number | boolean | null> = {}): void {
  if (experience.preview) return

  const attribution = getAttribution()
  const isChat = 'lockNode' in experience
  const chatLanding = isChat ? experience.landing as ChatLanding | undefined : undefined
  const eventId = crypto.randomUUID()
  const properties = {
    event_id: eventId,
    event_time: Math.floor(Date.now() / 1000),
    event_source_url: window.location.href,
    action_source: 'website',
    platform: 'web',
    app_surface: 'acquisition_web',
    story_id: experience.story.story_id,
    story_slug: experience.story.slug,
    story_version: experience.story.version,
    story_mode: experience.story.mode,
    landing_id: experience.landing?.landing_id ?? null,
    lock_id: isChat ? experience.lockNode : experience.lock.id,
    entry_mode: experience.entryMode,
    entry_point: experience.entryPoint,
    entry_block: isChat ? null : experience.entryBlock,
    entry_node: isChat ? experience.startNode : null,
    first_interactive_node: isChat ? experience.firstInteractiveNode : null,
    preloaded_node_count: isChat ? experience.preloadedNodes.length : 0,
    ad_last_node: chatLanding?.ad_last_node ?? null,
    has_entry_context: Boolean(experience.entryContext),
    lock_point: experience.lockPoint,
    lock_block: isChat ? null : experience.lockBlock,
    lock_node: isChat ? experience.lockNode : null,
    content_distance: experience.contentDistance,
    config_revision: experience.landing?.config_revision ?? null,
    landing_creative: experience.landing?.creative ?? null,
    session_id: getSessionId(),
    client_timestamp: new Date().toISOString(),
    ...attribution,
    creative: attribution.creative ?? experience.landing?.creative ?? null,
    ...extra,
  }

  captureMetaEvent(event, properties, eventId)
  if (enabled) {
    if (client) client.capture(event, properties)
    else queue.push({ event, properties })
  }
}

export const captureReaderEvent = captureStoryEvent
